/**
 * Agent-mode capabilities (FLO-451)
 *
 * The SDK ships three agents off one bundle, distinguished by two booleans:
 *
 *   demoMode     — the demo agent. Drives a SERVER-SIDE Playwright browser and
 *                  streams it back as video; the host page is just a frame.
 *   websiteAgent — the top-of-funnel Q&A chat. Drives nothing; it answers
 *                  questions about the page the visitor is already reading.
 *   (neither)    — the onboarding agent. Drives the HOST page directly: the
 *                  server resolves refs against the live DOM and dispatches
 *                  `ui_action` back for the client to click / fill / navigate.
 *
 * Host-page telemetry (`dom_snapshot`, `accessible_snapshot`, and the
 * click / scroll / focus / blur / form_input / keydown interaction metadata)
 * only exists to serve that last case. `demo_bot.py`'s `on_client_message`
 * handles four types — text_message, prospect_prefill, set_audio_output,
 * site_init — and has no `else`, so for the other two agents every one of
 * those messages is serialized, sent over the data channel, and dropped on
 * the floor. Capturing them also costs the customer's page a full layout +
 * style recalc (`dom-capture.ts` runs `querySelectorAll('*')` then
 * `getBoundingClientRect` per element), wired to a scroll handler.
 *
 * Derive the capability POSITIVELY and in one place. The gate used to be
 * `!config.demoMode`, which was correct until `websiteAgent` arrived
 * (FLO-320) and silently wrong from that day on. A negated per-mode boolean
 * fails open for every mode added after it; a named capability does not.
 *
 * FLO-404 introduces an agent registry — when it lands, this should read the
 * agent's declared capabilities rather than re-deriving them from flags.
 */

/** The subset of SDKConfig this predicate needs. */
export interface AgentModeFlags {
  demoMode?: boolean;
  websiteAgent?: boolean;
}

/**
 * True only for the onboarding agent — the one agent that acts on the host
 * page and therefore needs the host page's DOM and the visitor's interactions
 * streamed to the server.
 *
 * Gate every host-page capture on this, never on a negated mode flag.
 */
export function drivesHostPage(config: AgentModeFlags): boolean {
  return !config.demoMode && !config.websiteAgent;
}

/**
 * True for the two agents that sit on a customer's public marketing site: the
 * website agent and the demo agent (FLO-1096). The onboarding agent runs
 * inside a signed-in product, where a title, a heading or a button can carry
 * a customer's account data, so the visitor journey is not read there.
 *
 * Positive on purpose, like `drivesHostPage`: an agent added after these is
 * NOT on a marketing site until someone says so here.
 */
export const runsOnMarketingSite = (config: AgentModeFlags): boolean =>
  config.demoMode === true || config.websiteAgent === true;

/**
 * True only for the website agent: the Q&A chat on a marketing page, and the
 * one agent that shows contextual page nudges. `demoMode` wins when both flags are set (the
 * demo agent mounts instead), so this is false then.
 *
 * Positive on purpose, like `drivesHostPage`: an agent added after these is
 * not the website agent until someone says so here.
 */
export const isWebsiteAgent = (config: AgentModeFlags): boolean =>
  config.websiteAgent === true && config.demoMode !== true;

/**
 * Activation posture for the onboarding agent (FLO-534).
 *
 *   'proactive' — the collapsed launcher auto-shows an onboarding nudge for an
 *                 eligible user. A session still connects only after a click,
 *                 then opens by offering to get them set up.
 *   'on_demand' — "support mode". The launcher defaults to no automatic nudge,
 *                 but `nudge.autoShow: true` can opt back in. The agent remains
 *                 dormant until the user clicks it, then opens by asking what
 *                 they need. It can still run a walkthrough — the user asking
 *                 is the trigger.
 *
 * Lives here beside `drivesHostPage` because it is the same kind of fact: a
 * named posture derived once at the config boundary, not a flag re-read (and
 * re-interpreted) at each call site.
 */
export type ActivationMode = 'proactive' | 'on_demand';

const ACTIVATION_MODES: readonly ActivationMode[] = ['proactive', 'on_demand'];

/**
 * Normalise an untrusted `activation` value to the closed vocabulary.
 *
 * Fails toward 'proactive' on purpose. A typo, a stale build, or an absent
 * field must never silently mute a customer's onboarding agent — an ignored
 * flag is a visible bug someone reports, a dormant agent is a quiet outage.
 */
export function resolveActivation(value: unknown): ActivationMode {
  return ACTIVATION_MODES.includes(value as ActivationMode)
    ? (value as ActivationMode)
    : 'proactive';
}

/** True when the onboarding agent must stay dormant until the user opens it. */
export function isSupportMode(activation: unknown): boolean {
  return resolveActivation(activation) === 'on_demand';
}

/**
 * Should the collapsed orb slide out a nudge on its own? (FLO-534)
 *
 * The nudge is a top-of-funnel affordance. On the marketing site the agent is
 * *supposed* to reach out to a visitor who hasn't committed to anything — the
 * website and demo agents keep it on deliberately. In the product, a support
 * agent that taps the user on the shoulder five seconds after every page load
 * is the unprompted behaviour support mode exists to remove; "only speaks when
 * the user opens it" has to cover the bubble, not just the voice.
 *
 * Support mode therefore flips the DEFAULT, not the capability — a customer
 * who explicitly asks for `nudge: { autoShow: true }` still gets it.
 *
 * @param configured the customer's explicit `nudge.autoShow`, or undefined
 * @param activation the resolved (or raw) activation value
 */
export function resolveNudgeAutoShow(
  configured: boolean | undefined,
  activation: unknown,
): boolean {
  return configured ?? !isSupportMode(activation);
}
